import { HostActivityLogService } from '@components/pages/short-term-rental/shared/host-activity-log.service';
import { BookingInfoComponent } from '@components/shared/booking-info/booking-info.component';
import { EmptyStateComponent } from '@components/shared/empty-state/empty-state.component';
import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '@services/api/api.service';
import { LogService } from '@services/util/log.service';
import { FormatDatePipe } from 'app/pipes/format-date.pipe';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ParkingRequestFilter } from '../../models/parking-request-filter.model';
import { PaginatedParkingRequests, ParkingRequestRow } from '../../models/parking-request.model';
import { parkingPaymentGenerateMetadata, parkingRequestDeleteMetadata, parkingRowSnapshotOf } from '../../services/parking-activity.metadata';
import { ParkingRequestService } from '../../services/parking-request.service';
import { ParkingRequestFiltersComponent } from './parking-request-filters.component';

/**
 * Interface for table sort events
 */
interface SortEvent {
  field: string;
  order: number;
  data?: ParkingRequestRow[];
}

@Component({
  selector: 'app-parking-requests-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    PaginatorModule,
    TagModule,
    BadgeModule,
    MessageModule,
    PaginationComponent,
    BookingInfoComponent,
    FlatInfoComponent,
    EmptyStateComponent,
    ParkingRequestFiltersComponent,
    FormatDatePipe,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule
  ],
  providers: [ConfirmationService, MessageService],
  template: `
    <div>
      <!-- Toast for success/error messages -->
      <p-toast></p-toast>

      <!-- Confirmation Dialog for Delete Operations -->
      <p-confirmDialog></p-confirmDialog>

      <!-- Filter Panel -->
      <app-parking-request-filters
        [activeFilter]="activeFilter"
        (filterChange)="onFilterChange($event)"
      >
      </app-parking-request-filters>

      <!-- Loading Indicator -->
      @if (loading) {
        <div class="my-6 flex flex-col items-center justify-center">
          <!-- Tailwind Spinner -->
          <div class="relative h-14 w-14">
            <div class="absolute h-14 w-14 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
          </div>
          <span class="mt-3 font-medium">{{ 'common.loading' | translate }}</span>
        </div>
      }

      <!-- Error state -->
      @if (error$ | async; as errorMessage) {
        @if (errorMessage) {
          <div class="surface-danger border-round mb-3 p-4 text-white">
            {{ errorMessage }}
          </div>
        }
      }

      <!-- Table with parking requests (only rendered when data is available) -->
      @if (!loading && paginatedRequests && paginatedRequests.items.length > 0) {
        <div class="">
          <p-table
            #dt
            [value]="paginatedRequests.items"
            [stripedRows]="true"
            styleClass="p-datatable-sm"
            [rowHover]="true"
            [tableStyle]="{ 'min-width': '60rem' }"
            responsiveLayout="scroll"
            (sortFunction)="onSort($event)"
            [customSort]="true"
            dataKey="id"
          >
            <ng-template pTemplate="header">
              <tr>
                <th
                  [pSortableColumn]="'created_at'"
                  style="vertical-align:middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'parking.requests.tablehead.created_at' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'created_at' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'created_at' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'created_at') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th style="vertical-align:middle">
                  {{ 'parking.requests.tablehead.booking' | translate }}
                </th>
                <th
                  [pSortableColumn]="'start_date'"
                  style="vertical-align:middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'parking.requests.tablehead.start_date' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'start_date' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'start_date' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'start_date') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th
                  [pSortableColumn]="'end_date'"
                  style="vertical-align:middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'parking.requests.tablehead.end_date' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'end_date' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'end_date' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'end_date') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th style="vertical-align:middle">
                  {{ 'parking.requests.tablehead.parking_place' | translate }}
                </th>
                <th
                  [pSortableColumn]="'license_plate'"
                  style="vertical-align:middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'parking.requests.tablehead.license_plate' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'license_plate' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'license_plate' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'license_plate') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th
                  [pSortableColumn]="'paid'"
                  style="vertical-align:middle"
                >
                  <div class="flex items-center justify-between">
                    {{ 'parking.requests.tablehead.paid' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'paid' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'paid' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'paid') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th style="vertical-align:middle">
                  {{ 'parking.requests.tablehead.payment_transactions' | translate }}
                </th>
                <th style="vertical-align:middle">
                  {{ 'parking.requests.tablehead.invoice' | translate }}
                </th>
                <th style="vertical-align:middle">
                  {{ 'parking.requests.tablehead.actions' | translate }}
                </th>
              </tr>
            </ng-template>
            <ng-template
              pTemplate="body"
              let-request
            >
              <tr>
                <td class="whitespace-nowrap">{{ request.createdAt | formatDate }}</td>
                <td >
                  <app-booking-info [bookingInfo]="request.booking"></app-booking-info>
                </td>
                <td class="whitespace-nowrap">{{ request.startDate | formatDate }}</td>
                <td class="whitespace-nowrap">{{ request.endDate | formatDate }}</td>
                <td>
                  <app-flat-info
                    [flatInfo]="request.parkingPlaceInfo"
                    [isParkingPlace]="true"
                  ></app-flat-info>
                </td>
                <td>{{ request.licensePlate }}</td>
                <td>
                  <div class="align-items-center flex">
                    <span
                      class="inline-flex items-center justify-center whitespace-nowrap rounded-full px-2 py-1"
                      [ngClass]="{
                        'bg-green-100 text-green-800': request.paid,
                        'bg-yellow-100 text-yellow-800': !request.paid
                      }"
                    >
                      <span class="text-xs font-medium">
                        {{ (request.paid ? 'common.yes' : 'common.no') | translate }}
                      </span>
                    </span>
                  </div>
                </td>
                <td>
                  @if (request.paymentTransaction.id !== 0) {
                    <span class="inline-flex items-center gap-1 rounded-md px-2 py-1"
                          [class]="request.paymentTransaction.colorClasses">
                      <i [class]="request.paymentTransaction.iconClasses"></i>
                      <span class="text-xs font-medium">{{ request.paymentTransaction.id }}</span>
                    </span>
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (request.invoiceId && request.invoiceDisplay) {
                    <button
                      pButton
                      type="button"
                      [label]="request.invoiceDisplay"
                      class="p-button-link p-button-sm p-0 text-left"
                      (click)="navigateToInvoice(request.invoiceId, $event)"
                    ></button>
                  } @else if (request.invoiceDisplay) {
                    <span class="text-sm">{{ request.invoiceDisplay }}</span>
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  <div class="flex gap-2 justify-end">
                    @if (request.transactionCode) {
                      <button
                        pButton
                        icon="pi pi-copy"
                        class="p-button-rounded p-button-text p-button-sm p-button-success"
                        pTooltip="{{ 'parking.requests.copyPaymentLink' | translate }}"
                        tooltipPosition="top"
                        (click)="onCopyPaymentLink(request)"
                      ></button>
                    } @else if (!request.paid && !request.transactionCode) {
                      <button
                        pButton
                        icon="pi pi-credit-card"
                        class="p-button-rounded p-button-text p-button-sm p-button-info"
                        pTooltip="{{ 'parking.requests.generateTransaction' | translate }}"
                        tooltipPosition="top"
                        [disabled]="loading"
                        (click)="onGenerateTransaction(request)"
                      ></button>
                    }
                    <button
                      pButton
                      icon="pi pi-pencil"
                      class="p-button-rounded p-button-text p-button-sm"
                      (click)="onEdit(request)"
                      title="{{ 'common.edit' | translate }}"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-text p-button-sm p-button-danger"
                      (click)="onDelete(request)"
                      title="{{ 'common.delete' | translate }}"
                    ></button>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>

          <!-- Pagination controls -->
          <app-pagination
            [pageSize]="pageSize"
            [currentPage]="currentPage"
            [totalItems]="paginatedRequests ? paginatedRequests.totalCount : 0"
            (pageChange)="onPaginationChange($event)"
          >
          </app-pagination>
        </div>
      }

      <!-- Empty state when no items -->
      @if (!loading && (!paginatedRequests || paginatedRequests.items.length === 0)) {
        <app-empty-state
          [title]="'parking.requests.noRequests'"
          [description]="'parking.requests.noRequestsDescription'"
        >
        </app-empty-state>
      }
    </div>
  `
})
export class ParkingRequestsTableComponent implements OnInit, OnDestroy {
  // Default page size - matches API default
  readonly pageSize = 30;

  // Current page (1-based)
  currentPage = 1;

  // Paginator first record (0-based) - required for PrimeNG paginator
  first = 0;

  // Parking Requests data with pagination details
  paginatedRequests: PaginatedParkingRequests | null = null;

  // Loading state
  loading = false;

  // Current sort field and order
  sortField: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;

  // Keeps track of which column is sorted and in what direction
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: null,
    direction: null
  };

  // Active filters object
  activeFilter?: ParkingRequestFilter;

  // Error state
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  // Component cleanup
  private readonly destroy$ = new Subject<void>();
  private readonly hostActivityLog = inject(HostActivityLogService);

  constructor(
    private readonly parkingRequestService: ParkingRequestService,
    private readonly apiService: ApiService,
    private readonly log: LogService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadParkingRequests();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle filter changes from the filter component
   */
  onFilterChange(filter: ParkingRequestFilter): void {
    // Save active filter
    this.activeFilter = Object.keys(filter).length > 0 ? filter : undefined;
    this.log.debug('Applied parking request filters:', this.activeFilter);

    // Reset to first page when applying filters
    this.currentPage = 1;

    // Load with filters
    this.loadParkingRequests();
  }

  /**
   * Load parking requests for the current page with optional sort and filters
   */
  loadParkingRequests(): void {
    this.loading = true;
    this.errorSubject.next(null);

    // Prepare order parameter
    const orderField = this.sortField;
    const orderDirection = this.sortOrder;

    this.parkingRequestService
      .getParkingRequests(this.currentPage, this.pageSize, orderField, orderDirection, this.activeFilter)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (data: PaginatedParkingRequests): void => {
          this.paginatedRequests = data;
          this.log.debug('Loaded parking requests:', data);
        },
        error: (error: unknown): void => {
          this.errorSubject.next('Failed to load parking requests');
          this.log.error('Error loading parking requests:', error);
        }
      });
  }

  /**
   * Handle pagination event from shared pagination component
   */
  onPaginationChange(event: PaginationEvent): void {
    this.currentPage = event.page;
    this.first = (event.page - 1) * event.pageSize;
    this.loadParkingRequests();
  }

  /**
   * Calculate total number of pages based on total records and page size
   */
  getTotalPages(): number {
    return Math.ceil((this.paginatedRequests ? this.paginatedRequests.totalCount : 0) / this.pageSize);
  }

  /**
   * Custom sort handler for p-table
   */
  onSort(event: SortEvent): ParkingRequestRow[] {
    const clickedField = event.field;

    // If clicking on same column, toggle sort direction: none -> asc -> desc -> none
    if (clickedField === this.sortState.field) {
      // Toggle direction: null -> asc -> desc -> null
      if (this.sortState.direction === null) {
        this.sortState.direction = 'asc';
      } else if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else {
        // Reset sort completely when going beyond desc
        this.sortState.field = null;
        this.sortState.direction = null;
      }
    } else {
      // Clicking new column, start with asc
      this.sortState.field = clickedField;
      this.sortState.direction = 'asc';
    }

    // Save sort state for API requests
    this.sortField = this.sortState.field;
    this.sortOrder = this.sortState.direction;

    // Reload data with new sort
    this.loadParkingRequests();

    return event.data || [];
  }

  /**
   * Navigate to invoice detail page
   */
  navigateToInvoice(invoiceId: number, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/invoices', invoiceId]));
      window.open(url, '_blank');
    } else {
      this.router.navigate(['/invoices', invoiceId]);
    }
  }

  /**
   * Navigate to edit page for parking request
   */
  onEdit(request: ParkingRequestRow): void {
    this.log.debug('Edit request initiated for:', request);
    this.router.navigate(['/parking/request', request.id]);
  }

  onGenerateTransaction(request: ParkingRequestRow): void {
    this.loading = true;
    this.parkingRequestService
      .generatePaymentTransaction(request.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: () => {
          const metadata = parkingPaymentGenerateMetadata({
            parkingRequestId: request.id,
            bookingId: request.booking?.id ?? null,
            flats: request.bookingFlats
          });
          this.hostActivityLog.log('parking_payment_generate', metadata);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.success'),
            detail: this.translateService.instant('parking.requests.generateTransaction.success')
          });
          this.loadParkingRequests();
        },
        error: (error: unknown) => {
          this.log.error('Error generating payment transaction:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('parking.requests.generateTransaction.error')
          });
        }
      });
  }

  onCopyPaymentLink(request: ParkingRequestRow): void {
    if (!request.transactionCode) return;
    const paymentLink = this.generatePaymentLink(request.transactionCode, request.bookingNationality);
    navigator.clipboard.writeText(paymentLink).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.success'),
        detail: this.translateService.instant('parking.requests.paymentLinkCopied')
      });
    });
  }

  private generatePaymentLink(transactionCode: string, nationality?: string): string {
    const baseUrl = this.apiService.getBaseUrl();
    const domain = baseUrl.includes('teszt') ? 'teszt.i11.hu' : 'i11.hu';
    const locale = nationality === 'HU' ? 'hu' : 'en';
    return `https://${domain}/${locale}/payment/${transactionCode}/`;
  }

  /**
   * Delete request with confirmation
   */
  onDelete(request: ParkingRequestRow): void {
    this.log.debug('Delete request initiated for:', request);

    this.confirmationService.confirm({
      message: this.translateService.instant('parking.requests.delete.confirmMessage'),
      header: this.translateService.instant('common.delete.confirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.yesDelete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.loading = true;

        this.parkingRequestService
          .deleteParkingRequest(request.id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (deleted: boolean) => {
              if (deleted) {
                const before = parkingRowSnapshotOf(request);
                const metadata = parkingRequestDeleteMetadata({ parkingRequestId: request.id, before, origin: 'parking_list' });
                this.hostActivityLog.log('parking_request_delete', metadata);
              }
              this.messageService.add({
                severity: deleted ? 'success' : 'error',
                summary: this.translateService.instant(deleted ? 'common.success' : 'common.error'),
                detail: this.translateService.instant(deleted ? 'parking.requests.delete.success' : 'parking.requests.delete.error')
              });
              this.loadParkingRequests();
            },
            error: (error: unknown) => {
              this.log.error('Error deleting parking request:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.error'),
                detail: this.translateService.instant('parking.requests.delete.error')
              });
            }
          });
      }
    });
  }
}
